/* eslint-disable @trello/filename-case */
/* eslint-disable @trello/export-matches-filename */

import { euCountries, usTerritories } from './countries';

/**
 * Checks for postal codes that begin with G, H or J in Canada
 * @see https://en.wikipedia.org/wiki/Postal_codes_in_Canada
 */
export const isQuebec = (country: string, zipCode?: string): boolean => {
  return country === 'CA' && /^[GHJ]/i.test(zipCode || '');
};

/**
 * Check if the country accepts a Tax ID number
 * - EU countries, Norway and The Republic of Korea accepts a VAT number
 * - Australia accepts an ABN number
 * - New Zealand accepts a GST number
 * - Quebec, Canada accepts a QST number
 *
 * Should mimic similar validation logic in shop. See
 * @see https://bitbucket.org/trello/aardvark/src/master/Aardvark/Accounting/CAvalara.cs
 */
export const acceptsTaxId = (country: string, zipCode?: string): boolean => {
  return (
    euCountries.indexOf(country) !== -1 ||
    isQuebec(country, zipCode) ||
    ['AU', 'KR', 'NO', 'NZ'].indexOf(country) !== -1
  );
};

// Keeping this alias for backwards-compat with old coffee module until refactor is complete
export const hasTaxNumbers = acceptsTaxId;

/**
 * Returns the appropriate abbreviation for the type of Tax ID required by
 * the given country / zipCode combination
 */
export const getTaxType = (
  country: string,
  zipCode?: string,
): 'ABN' | 'GST' | 'QST' | 'VAT' | '' => {
  if (!acceptsTaxId(country, zipCode)) {
    return '';
  }
  if (country === 'AU') {
    return 'ABN';
  } else if (country === 'NZ') {
    return 'GST';
  } else if (isQuebec(country, zipCode)) {
    return 'QST';
  } else {
    return 'VAT';
  }
};

/**
 * For a country / zip code combination that requires a tax ID number,
 * checks that it is within the valid length range. Does not attempt to
 * validate the taxable entity or do any further pattern matching than
 * just checking the length. Proper validation is done server-side.
 *
 * @see https://bitbucket.org/trello/aardvark/src/master/Aardvark/Accounting/CAvalara.cs
 * @see https://en.wikipedia.org/wiki/VAT_identification_number
 */
export const isValidTaxId = (
  taxId: string = '',
  country: string = '',
  zipCode?: string,
): boolean => {
  if (!acceptsTaxId(country, zipCode)) {
    return true;
  }
  if (taxId && taxId.length) {
    const taxDigitsLength = taxId.replace(/\D/g, '').length;
    const maxLength = isQuebec(country, zipCode) ? 14 : 12;
    return taxDigitsLength >= 2 && taxDigitsLength <= maxLength;
  } else {
    return true;
  }
};

/**
 * Checks if a given country requires a zip code to be entered. These include
 * - United States
 * - US Territories
 * - Canada
 */
export const requiresZipCode = (country: string): boolean => {
  return (
    country === 'US' || country === 'CA' || usTerritories.includes(country)
  );
};

/**
 * For a country that requires a zip code, validates that the given
 * zip code looks valid
 * USA format: 5 digits
 *  - Shop does not appear to support 9 digit zip codes
 * Canadian format: A1A-1A1
 *  - Shop does not validate Canadian zip code formats nor does it require them to
 *    be entered at all. It just forwards them to Avalara. The only restriction is
 *    that it is 11 characters or less
 */
export const isValidZipCode = (zipCode: string, country: string): boolean => {
  if (country === 'US' || usTerritories.includes(country)) {
    const ref = zipCode.replace(/\D/g, '').length;
    return ref === 5;
  } else if (country === 'CA') {
    return zipCode.length > 0 && zipCode.length <= 11;
  } else {
    return false;
  }
};
